import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  DatePicker,
  message,
  Tooltip,
  Divider,
  Empty,
  Select,
  Modal,
  Button,
} from "antd";
import Search from "antd/es/input/Search";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { PrinterOutlined, CloseOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import {
  HEADERS_EXPORT_BEN,
  POST_DETAIL_PAYMENT,
  POST_DETAIL_PAYMENT_LEASING,
} from "../../../API/apiUrls";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  faMapLocationDot,
  faCarSide,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { optionsLocat } from "../../../../utils/constant/LocatOption";
import ThaiDatePicker from "../../../../hook/ThaiDatePicker";

// ============ Helper Functions ============

/**
 * แปลง schema และ product เป็น data type
 */
const getDataTypeFromSchema = (schema, product) => {
  const schemaMap = {
    vsfhp: "VSFHP",
    lsfhp: "LSFHP",
    psfhp: "PSFHP",
    rpsl: "RPSL",
  };

  if (schema === "sfhp") {
    return product === 1
      ? "SFHP_CAR"
      : product === 2
        ? "SFHP_LAND"
        : "SFHP_CAR";
  }

  return schemaMap[schema] || null;
};

/**
 * สร้าง options สำหรับประเภทสัญญา
 */
const getContractOptions = (userCompany) => {
  if (userCompany === "3") {
    return [
      { value: "KSM", label: "KSM(ที่ดิน)" },
      { value: "RPSL", label: "P-Loan" },
    ];
  }

  return [
    { value: "LSFHP", label: "สัญญา 1" },
    { value: "VSFHP", label: "สัญญา 2" },
    { value: "PSFHP", label: "สัญญา 3(เก่า)" },
    { value: "RPSL", label: "สัญญา 3(ใหม่)" },
    { value: "SFHP_LAND", label: "สัญญา 8(ที่ดิน)" },
    { value: "SFHP_CAR", label: "สัญญา 8(รถ)" },
  ];
};

/**
 * ตรวจสอบสิทธิ์การเข้าถึงข้อมูล
 */
const checkAccessPermission = (item, userCompany, optionsLocat) => {
  if (!item) return false;

  const branch = (item.locat || "").toString().trim().toUpperCase();
  const contno = (item.contno || "").toString().trim().toUpperCase();
  const labels = (optionsLocat || []).map((opt) =>
    (opt.label || "").toString().trim().toUpperCase(),
  );

  if (userCompany === "3") {
    if (!branch) return true;
    return !labels.some((lbl) => branch.includes(lbl)) || contno.includes("UD");
  }

  if (!branch) return false;
  return labels.some((lbl) => branch.includes(lbl));
};

/**
 * Format date with safety check
 */
const formatDateSafe = (dateStr, format = "DD/MM/YYYY") => {
  try {
    const date = dayjs(dateStr);
    return date.isValid() ? date.format(format) : "-";
  } catch (e) {
    return "-";
  }
};

/**
 * คำนวณยอดรวมสำหรับข้อมูลปกติ
 */
const calculateTotalData = (data) => {
  return data.reduce(
    (acc, item) => ({
      totalDays: acc.totalDays + (item.Days || 0),
      totalPayment: acc.totalPayment + (item.NETPAY || 0),
      totalDUEINTEFF: acc.totalDUEINTEFF + (item.DUEINTEFF || 0),
      totalDUETONEFF: acc.totalDUETONEFF + (item.DUETONEFF || 0),
      totalKongdok:
        acc.totalKongdok + ((item.DUEINTEFF || 0) - (item.KangDok || 0)),
    }),
    {
      totalDays: 0,
      totalPayment: 0,
      totalDUEINTEFF: 0,
      totalDUETONEFF: 0,
      totalKongdok: 0,
    },
  );
};

/**
 * คำนวณยอดรวมสำหรับข้อมูล Leasing
 */
const calculateLeasingData = (dataTable, arrData) => {
  const totalLateDate = dataTable.reduce(
    (sum, item) => sum + (item.late_days || 0),
    0,
  );
  const totalNoPaid = dataTable.reduce(
    (sum, item) => sum + (item.remaining || 0),
    0,
  );
  const totalPaid = dataTable.reduce((sum, item) => sum + (item.paid || 0), 0);

  const dataPaidLastNopay =
    [...dataTable]?.reverse().find((item) => Number(item.paid) !== 0) || {};

  const [nopayStart = 0, nopayEnd = 0] = String(
    arrData?.loan?.unpaid_count || "0-0",
  )
    .split("-")
    .map((num) => Number(num));

  const resultNopay = dataPaidLastNopay.damt
    ? (nopayEnd - nopayStart + 1) * Number(dataPaidLastNopay.damt) -
      Number(dataPaidLastNopay.paid || 0) +
      Number(dataPaidLastNopay.remaining || 0)
    : 0;

  return {
    totalLateDate,
    totalNoPaid,
    totalPaid,
    resultNopay,
  };
};

/**
 * ตรวจสอบว่าเป็นประเภท Leasing หรือไม่
 */
const isLeasingType = (dataType) => {
  return dataType === "VSFHP" || dataType === "SFHP_CAR";
};

// ============ Main Component ============

const DebtPayment = ({
  open,
  close,
  initialContractNo = null,
  initialDataType = null,
  dataDefault,
}) => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();

  const userCompany = localStorage.getItem("COMPANY_ID");

  // States
  const [loading, setLoading] = useState(false);
  const [arrayTable, setArrayTable] = useState(null);
  const [arrData, setArrData] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [queryContno, setQueryContno] = useState(initialContractNo);
  const [date, setDate] = useState(dayjs());
  const [dataTypeSelect, setDataTypeSelect] = useState(null);

  // Memoized values
  const optionsDataType = useMemo(
    () => getContractOptions(userCompany),
    [userCompany],
  );

  const result = useMemo(() => {
    if (!arrayTable || isLeasingType(dataTypeSelect)) return null;
    const dataToCalculate = selectedRows.length > 0 ? selectedRows : arrayTable;
    return calculateTotalData(dataToCalculate);
  }, [arrayTable, selectedRows, dataTypeSelect]);

  const resultLeasing = useMemo(() => {
    if (!arrayTable || !isLeasingType(dataTypeSelect)) return null;
    return calculateLeasingData(arrayTable, arrData);
  }, [arrayTable, arrData, dataTypeSelect]);

  // ============ API Functions ============

  const queryData = useCallback(
    async (contno, typeValue) => {
      setLoading(true);
      const apiUse =
        isLeasingType(typeValue) || typeValue === "SFHP_LAND"
          ? POST_DETAIL_PAYMENT_LEASING
          : POST_DETAIL_PAYMENT;

      try {
        const response = await axios.post(
          apiUse,
          {
            contno,
            todate: dayjs(date).format("YYYY-MM-DD"),
            type:
              typeValue === "SFHP_LAND" || typeValue === "SFHP_CAR"
                ? "SFHP"
                : typeValue,
          },
          { headers: HEADERS_EXPORT_BEN },
        );

        if (response.status === 200 && response.data?.[0]) {
          const rawData = response.data[0];

          // ตรวจสอบสิทธิ์สำหรับ RPSL
          if (typeValue === "RPSL") {
            const hasPermission = checkAccessPermission(
              rawData.chqtran?.[0],
              userCompany,
              optionsLocat,
            );
            if (!hasPermission) {
              message.error("ไม่มีสิทธิ์เข้าถึงข้อมูล");
              setArrayTable(null);
              setArrData(null);
              return;
            }
          }

          // จัดเตรียมข้อมูลตาราง
          const sourceData = isLeasingType(typeValue)
            ? rawData.result
            : rawData.resultdata;

          const formattedData = sourceData?.map((item, index) => ({
            ...item,
            key: index + 1,
          }));

          setArrayTable(formattedData);
          setArrData(rawData);
          message.success("โหลดข้อมูลสำเร็จ");
        } else {
          throw new Error("ไม่พบข้อมูล");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        const errorMessage =
          error.response?.status === 404
            ? "ไม่มีเลขที่สัญญาที่ค้นหา"
            : error.message || "เกิดข้อผิดพลาดในการดึงข้อมูล";

        message.error(errorMessage);
        setArrayTable(null);
        setArrData(null);
      } finally {
        setLoading(false);
      }
    },
    [date, userCompany],
  );

  // ============ Event Handlers ============

  const handleSearch = useCallback(() => {
    if (queryContno && dataTypeSelect) {
      queryData(queryContno, dataTypeSelect);
    } else {
      message.warning("กรุณาเลือกประเภทสัญญาและกรอกเลขที่สัญญา");
    }
  }, [queryContno, dataTypeSelect, queryData]);

  const handleDateChange = useCallback((newDate) => {
    setDate(newDate);
  }, []);

  const handleContnoChange = useCallback((value) => {
    setQueryContno(value);
    if (!value) {
      setArrData(null);
      setArrayTable(null);
    }
  }, []);

  const handleTypeChange = useCallback((value) => {
    setDataTypeSelect(value);
    setArrayTable(null);
    setArrData(null);
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, []);

  const handleSelectChange = useCallback((rowKeys, rows) => {
    setSelectedRowKeys(rowKeys);
    setSelectedRows(rows);
  }, []);

  // ============ Effects ============

  // Auto load data when modal opens
  useEffect(() => {
    if (open && dataDefault?.CONTNO && dataDefault?.SCHEMA) {
      const dataType = getDataTypeFromSchema(
        dataDefault.SCHEMA,
        dataDefault.product,
      );

      if (dataType) {
        setDataTypeSelect(dataType);
        setQueryContno(dataDefault.CONTNO);
        queryData(dataDefault.CONTNO, dataType);
      }
    }
  }, [open, dataDefault, queryData]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  }, [open]);

  // ============ PDF Generation ============

  const generatePDF = useCallback(() => {
    if (!arrData?.resultdata) {
      message.error("ไม่สามารถพิมพ์รายงานได้!");
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setFont("THSarabunNew", "bold");
    doc.text("รายละเอียดสัญญา", 105, 15, null, null, "center");

    let yLine = 20;
    doc.setFontSize(14);
    doc.text(
      `ผู้ทำสัญญา : ${arrData?.customer[0]?.name || "-"}`,
      105,
      yLine,
      null,
      null,
      "center",
    );

    // เพิ่มข้อมูลผู้ค้ำ
    doc.setFont("THSarabunNew", "normal");
    if (
      arrData?.guarantor?.length > 0 &&
      queryContno?.substring(0, 1) !== "1"
    ) {
      arrData.guarantor.forEach((data, index) => {
        yLine += 6;
        doc.text(
          `คนค้ำที่ ${index + 1}: ${data.name}`,
          105,
          yLine,
          null,
          null,
          "center",
        );
      });
    }

    // ข้อมูลสัญญาและทรัพย์สิน
    yLine += 5;
    const isLandContract =
      queryContno?.substring(0, 1) === "3" || dataTypeSelect === "SFHP_CAR";

    doc.text(
      `เลขที่สัญญา: ${arrData?.chqtran[0]?.contno || "-"}`,
      50,
      yLine + 5,
    );

    if (!isLandContract) {
      doc.text(`จังหวัด: ${arrData?.invtran?.type || "-"}`, 50, yLine + 10);
      doc.text(`อำเภอ: ${arrData?.invtran?.modeldes || "-"}`, 50, yLine + 15);
      doc.text(`ประเภท: ${arrData?.invtran?.color || "-"}`, 50, yLine + 20);
      doc.text(`เลขโฉนด: ${arrData?.invtran?.strno || "-"}`, 50, yLine + 25);
    }

    // ข้อมูลการเงิน
    doc.text(
      `วันเริ่มทำสัญญา: ${formatDateSafe(arrData?.loan?.sdate)}`,
      120,
      yLine + 5,
    );

    const firstPaymentDate =
      arrData?.arpay?.[0]?.ddate || arrData?.resultdata?.[0]?.Inpdt;
    doc.text(
      `${
        dataTypeSelect !== "KSM" ? "กำหนดชำระงวดแรก:" : "ชำระครั้งแรกเมื่อ:"
      } ${formatDateSafe(firstPaymentDate)}`,
      120,
      yLine + 10,
    );

    doc.text(
      `ยอดกู้: ${currencyFormatPoint(arrData?.loan?.ncshprc) || "-"} บาท`,
      120,
      yLine + 15,
    );

    // วันที่คิดดอกเบี้ย
    const interestStartDate =
      arrData?.loan?.flag === 1
        ? arrData?.loan?.startdate
        : dayjs(arrData?.loan?.sdate).add(1, "day").format("YYYY-MM-DD");

    doc.text(
      `วันที่คิดดอกเบี้ย: ${formatDateSafe(
        interestStartDate,
      )} - ${formatDateSafe(arrData?.loan?.enddate)}`,
      120,
      yLine + 20,
    );

    // ยอดเงินที่ค้าง (สีแดง)
    doc.setTextColor(255, 0, 0);
    doc.text(
      `ต้นคงเหลือ: ${currencyFormatPoint(arrData?.loan?.tonkong) || "-"} บาท`,
      120,
      yLine + 25,
    );

    const kangdokTotal =
      arrData?.loan?.flag === 1
        ? arrData?.loan?.kangdok + arrData?.loan?.dok
        : arrData?.loan?.kangdok;

    doc.text(
      `ค้างดอกเบี้ย: ${currencyFormatPoint(kangdokTotal)} บาท`,
      120,
      yLine + 30,
    );

    doc.text(
      `รวมทุนฟ้อง: ${currencyFormatPoint(
        kangdokTotal + arrData?.loan?.tonkong,
      )} บาท`,
      120,
      yLine + 35,
    );

    // สร้างตาราง
    const tableColumn = [
      "ลำดับ",
      "วันที่ชำระ",
      "จำนวนวันที่ค้าง",
      "ยอดเงินที่ชำระ",
      "ดอกเบี้ย",
      "เงินต้น",
      "ดอกเบี้ยที่ค้าง",
      "ต้นคงเหลือ",
    ];

    const tableRows = [];
    const dataToExport = selectedRows.length > 0 ? selectedRows : arrayTable;

    let totals = {
      days: 0,
      payment: 0,
      interest: 0,
      principal: 0,
      kangdok: 0,
    };

    dataToExport.forEach((item, index) => {
      tableRows.push([
        index + 1,
        arrData.loan.flag === 1 ? formatDateSafe(item.Inpdt) : "-",
        item.Days || 0,
        currencyFormatPoint(item.NETPAY) || 0,
        currencyFormatPoint(item.DUEINTEFF) || 0,
        currencyFormatPoint(item.DUETONEFF) || 0,
        currencyFormatPoint(item.KangDok) || 0,
        currencyFormatPoint(item.Ton) || 0,
      ]);

      totals.days += Number(item.Days || 0);
      totals.payment += Number(item.NETPAY || 0);
      totals.interest += Number(item.DUEINTEFF || 0);
      totals.principal += Number(item.DUETONEFF || 0);
      totals.kangdok += Number(item.KangDok || 0);
    });

    // แถวสรุป
    tableRows.push([
      "",
      "รวมทั้งหมด",
      `${currencyFormatComma(totals.days)} วัน`,
      `${currencyFormatPoint(totals.payment)} บาท`,
      `${currencyFormatPoint(totals.interest)} บาท`,
      `${currencyFormatPoint(totals.principal)} บาท`,
      `${currencyFormatPoint(totals.kangdok)} บาท`,
      "",
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: yLine + 40,
      theme: "striped",
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        textColor: [0, 0, 0],
        halign: "center",
      },
      headStyles: { fillColor: [0, 0, 139], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [173, 216, 230] },
      didParseCell: function (data) {
        if (
          data.row.cells[1]?.raw === "รวมทั้งหมด" &&
          data.section === "body"
        ) {
          data.cell.styles.textColor = [0, 0, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = "14";
        }
      },
    });

    // เพิ่มตารางนัดชำระ (ถ้ามี)
    if (arrData?.arpay) {
      doc.addPage();
      let yLineSchedule = 20;

      doc.setFontSize(16);
      doc.setFont("THSarabunNew", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(
        `ตารางนัดชำระสัญญา ${arrData?.arpay[0]?.contno}`,
        105,
        yLineSchedule,
        null,
        null,
        "center",
      );

      yLineSchedule += 5;
      doc.setFontSize(14);
      doc.text(
        `ผู้ทำสัญญา : ${arrData?.customer[0]?.name}`,
        105,
        yLineSchedule,
        null,
        null,
        "center",
      );

      doc.setFont("THSarabunNew", "normal");
      if (
        arrData?.guarantor?.length > 0 &&
        queryContno?.substring(0, 1) !== "1"
      ) {
        arrData.guarantor.forEach((data, index) => {
          yLineSchedule += 6;
          doc.text(
            `คนค้ำที่ ${index + 1}: ${data.name}`,
            105,
            yLineSchedule,
            null,
            null,
            "center",
          );
        });
      }

      const scheduleColumn = [
        "งวดที่",
        "วันที่นัดชำระ",
        "ยอดที่ต้องชำระ (บาท)",
      ];
      const scheduleRows = arrData.arpay.map((item) => [
        item.nopay,
        formatDateSafe(item.ddate),
        currencyFormatPoint(item.damt),
      ]);

      yLineSchedule += 5;
      doc.autoTable({
        head: [scheduleColumn],
        body: scheduleRows,
        startY: yLineSchedule,
        theme: "striped",
        styles: {
          font: "THSarabunNew",
          fontSize: 12,
          halign: "center",
        },
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
        },
        alternateRowStyles: {
          fillColor: [227, 242, 253],
        },
      });
    }

    doc.save(`ข้อมูลสัญญา ${queryContno}.pdf`);
  }, [
    arrData,
    queryContno,
    dataTypeSelect,
    selectedRows,
    arrayTable,
    currencyFormatPoint,
    currencyFormatComma,
  ]);

  const generateLeasingDataPDF = useCallback(() => {
    if (!arrData?.result) {
      message.error("ไม่สามารถพิมพ์รายงานได้!");
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setFont("THSarabunNew", "bold");
    doc.text("รายละเอียดสัญญา", 105, 15, null, null, "center");

    let yLine = 20;
    doc.setFontSize(14);
    doc.text(
      `ผู้ทำสัญญา : ${arrData?.customer[0]?.name || "-"}`,
      105,
      yLine,
      null,
      null,
      "center",
    );

    // เพิ่มข้อมูลผู้ค้ำ
    doc.setFont("THSarabunNew", "normal");
    if (
      arrData?.guarantor?.length > 0 &&
      queryContno?.substring(0, 1) !== "1"
    ) {
      arrData.guarantor.forEach((data, index) => {
        yLine += 6;
        doc.text(
          `คนค้ำที่ ${index + 1}: ${data.name || "-"}`,
          105,
          yLine,
          null,
          null,
          "center",
        );
      });
    }

    // ข้อมูลทรัพย์สิน
    yLine += 5;
    doc.text(
      `เลขที่สัญญา: ${arrData?.customer[0]?.contno || "-"}`,
      50,
      yLine + 5,
    );
    doc.text(`ประเภท : ${arrData?.invtran?.baabdes || "-"}`, 50, yLine + 10);
    doc.text(`รุ่น: ${arrData?.invtran?.modeldes || "-"}`, 50, yLine + 15);
    doc.text(`สี: ${arrData?.invtran?.color || "-"}`, 50, yLine + 20);
    doc.text(`เลขตัวถัง: ${arrData?.invtran?.strno || "-"}`, 50, yLine + 25);
    doc.text(
      `ทะเบียน: ${arrData?.invtran?.regno || "-"} ${
        arrData?.invtran?.dorecv || ""
      }`,
      50,
      yLine + 30,
    );
    doc.text(`ปีจดทะเบียน: ${arrData?.invtran?.manuyr || "-"}`, 50, yLine + 35);

    // ข้อมูลการเงิน
    doc.text(
      `วันเริ่มทำสัญญา: ${formatDateSafe(arrData?.loan?.sdate)}`,
      120,
      yLine + 5,
    );
    doc.text(
      `ชำระงวดแรกเมื่อ: ${formatDateSafe(arrData?.result[0]?.ddate)}`,
      120,
      yLine + 10,
    );
    doc.text(
      `ยอดเช่าซื้อ: ${currencyFormatPoint(arrData?.loan?.balanc) || "-"} บาท`,
      120,
      yLine + 15,
    );
    doc.text(
      `เงินลงทุน: ${currencyFormatPoint(arrData?.loan?.ncshprc) || "-"} บาท`,
      120,
      yLine + 20,
    );

    doc.setTextColor(255, 0, 0);
    doc.text(
      `ผ่อนทั้งหมด: ${arrData?.loan?.tnopay || "-"} งวด`,
      120,
      yLine + 25,
    );
    doc.text(
      `จ่ายมาทั้งหมด: ${currencyFormatPoint(
        arrData?.loan?.total_used_net_pay,
      )} บาท`,
      120,
      yLine + 30,
    );
    doc.text(
      `ลูกหนี้คงเหลือ: ${currencyFormatPoint(
        arrData?.loan?.remaining_debt,
      )} บาท`,
      120,
      yLine + 35,
    );

    // สร้างตาราง
    const tableColumn = [
      "งวดที่",
      "วันดิว",
      "ค่างวด",
      "วันที่ชำระ",
      "จำนวนรับ",
      "จำนวนวันล่าช้า",
      "เงินค้าง",
      "ลูกหนี้คงเหลือ",
    ];

    const tableRows = [];
    const dataToExport = selectedRows.length > 0 ? selectedRows : arrayTable;

    let totals = {
      lateDate: 0,
      noPaid: 0,
      paid: 0,
    };

    let previousNoPay = null;
    let previousDdate = null;
    let previousDamt = null;

    dataToExport.forEach((item) => {
      const showNoPay = item?.nopay !== previousNoPay ? item?.nopay : "";
      const showDdate = item?.ddate !== previousDdate ? item?.ddate : "";
      const showDamt = item?.nopay !== previousNoPay ? item?.damt : "";

      tableRows.push([
        showNoPay || "",
        showDdate ? formatDateSafe(showDdate) : "",
        showDamt ? currencyFormatPoint(showDamt) : "",
        formatDateSafe(item?.pay_date) || 0,
        currencyFormatPoint(item?.paid) || 0,
        item?.late_days || 0,
        currencyFormatPoint(item?.remaining) || 0,
        currencyFormatPoint(item?.remain_debt_total) || 0,
      ]);

      previousNoPay = item.nopay;
      previousDdate = item?.ddate;
      previousDamt = item?.damt;

      totals.lateDate += Number(item.late_days || 0);
      totals.noPaid += Number(item.remaining || 0);
      totals.paid += Number(item.paid || 0);
    });

    // แถวสรุป
    tableRows.push([
      "ค้างค่างวด",
      `฿${currencyFormatPoint(resultLeasing.resultNopay)}`,
      `${arrData?.loan.unpaid_count} งวด`,
      "",
      `฿${currencyFormatPoint(totals.paid)}`,
      `${currencyFormatComma(totals.lateDate)} วัน`,
      `฿${currencyFormatPoint(totals.noPaid)}`,
      `฿${currencyFormatPoint(arrData?.loan?.remaining_debt)}`,
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: yLine + 40,
      theme: "striped",
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        textColor: [0, 0, 0],
        halign: "center",
      },
      headStyles: { fillColor: [0, 0, 139], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [173, 216, 230] },
      didParseCell: function (data) {
        if (
          data.row.cells[0]?.raw === "ค้างค่างวด" &&
          data.section === "body"
        ) {
          data.cell.styles.textColor = "blue";
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = "14";
        }
      },
    });

    doc.save(`ข้อมูลสัญญา ${queryContno}.pdf`);
  }, [
    arrData,
    queryContno,
    selectedRows,
    arrayTable,
    resultLeasing,
    currencyFormatPoint,
    currencyFormatComma,
  ]);

  // ============ Table Columns ============

  const columns = useMemo(() => {
    if (isLeasingType(dataTypeSelect)) {
      return [
        {
          title: "งวดที่",
          key: "nopay",
          align: "center",
          render: (text, record, index) => {
            const sameNopay = arrayTable?.filter(
              (item) => item.nopay === record.nopay,
            );
            const firstIndex = arrayTable?.findIndex(
              (item) => item.nopay === record.nopay,
            );

            if (index === firstIndex) {
              return {
                children: record.nopay,
                props: { rowSpan: sameNopay.length },
              };
            }
            return { children: null, props: { rowSpan: 0 } };
          },
        },
        {
          title: "วันดิว",
          align: "center",
          render: (text, record, index) => {
            const sameDdate = arrayTable?.filter(
              (item) => item.ddate === record.ddate,
            );
            const firstIndex = arrayTable?.findIndex(
              (item) => item.ddate === record.ddate,
            );

            if (index === firstIndex) {
              return {
                children: formatDateSafe(record.ddate),
                props: { rowSpan: sameDdate.length },
              };
            }
            return { children: null, props: { rowSpan: 0 } };
          },
        },
        {
          title: "ค่างวด",
          align: "center",
          render: (text, record, index) => {
            const sameDdate = arrayTable?.filter(
              (item) => item.nopay === record.nopay,
            );
            const firstIndex = arrayTable?.findIndex(
              (item) => item.nopay === record.nopay,
            );

            if (index === firstIndex) {
              return {
                children: currencyFormatPoint(record?.damt),
                props: { rowSpan: sameDdate.length },
              };
            }
            return { children: null, props: { rowSpan: 0 } };
          },
        },
        {
          title: "วันที่ชำระ",
          align: "center",
          render: (record) => formatDateSafe(record?.pay_date) || null,
        },
        {
          title: "จำนวนรับ",
          align: "center",
          render: (record) => currencyFormatPoint(record?.paid) || 0,
        },
        {
          title: "จำนวนวันล่าช้า",
          align: "center",
          render: (record) => record?.late_days || 0,
        },
        {
          title: "เงินค้าง",
          align: "center",
          render: (record) => currencyFormatPoint(record?.remaining) || 0,
        },
        {
          title: "ลูกหนี้คงเหลือ",
          align: "center",
          render: (record) =>
            currencyFormatPoint(record?.remain_debt_total) || 0,
        },
      ];
    }

    return [
      {
        title: "ลำดับ",
        key: "index",
        align: "center",
        render: (text, record, index) => index + 1,
      },
      {
        title: "วันที่ชำระ",
        align: "center",
        render: (record) => formatDateSafe(record?.Inpdt) || null,
      },
      {
        title: "จำนวนวันที่ค้าง",
        align: "center",
        render: (record) => record?.Days || "-",
      },
      {
        title: "ยอดเงินที่ชำระ",
        align: "center",
        render: (record) => currencyFormatComma(record?.NETPAY) || 0,
      },
      {
        title: "ดอกเบี้ย",
        align: "center",
        render: (record) => currencyFormatPoint(record?.DUEINTEFF) || 0,
      },
      {
        title: "เงินต้น",
        align: "center",
        render: (record) => currencyFormatPoint(record?.DUETONEFF) || 0,
      },
      {
        title: "ดอกเบี้ยที่ค้าง",
        align: "center",
        render: (record) => currencyFormatPoint(record?.KangDok) || 0,
      },
      {
        title: "ต้นคงเหลือ",
        align: "center",
        render: (record) => currencyFormatPoint(record.Ton) || 0,
      },
    ];
  }, [dataTypeSelect, arrayTable, currencyFormatPoint, currencyFormatComma]);

  // ============ Row Selection ============

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: handleSelectChange,
    }),
    [selectedRowKeys, handleSelectChange],
  );

  // ============ Render Helpers ============

  const renderContractDetails = () => {
    if (!arrData) return null;

    const isCarOrLeasing = isLeasingType(dataTypeSelect);
    const contractPrefix = queryContno?.substring(0, 1);

    return (
      <Row gutter={[16, 16]}>
        <Col span={12} style={{ textAlign: "center" }}>
          <p>
            <b>เลขที่สัญญา: </b>
            {arrData?.customer?.[0]?.contno || "-"}
          </p>

          {isCarOrLeasing ? (
            <>
              <p>
                <b>ประเภท: </b> {arrData?.invtran?.baabdes || "-"}
              </p>
              <p>
                <b>รุ่น: </b> {arrData?.invtran?.modeldes || "-"}
              </p>
              <p>
                <b>สี: </b> {arrData?.invtran?.color || "-"}
              </p>
              <p>
                <b>เลขตัวถัง: </b> {arrData?.invtran?.strno || "-"}
              </p>
              <p>
                <b>ทะเบียน: </b> {arrData?.invtran?.regno || "-"}{" "}
                {arrData?.invtran?.dorecv || ""}
              </p>
              <p>
                <b>ปีจดทะเบียน: </b> {arrData?.invtran?.manuyr || "-"}
              </p>
            </>
          ) : (
            <>
              <p>
                <b>{contractPrefix === "3" ? "ประเภท" : "จังหวัด"}: </b>
                {arrData?.invtran?.type || "-"}
              </p>
              <p>
                <b>{contractPrefix === "3" ? "รุ่น" : "อำเภอ"}: </b>
                {arrData?.invtran?.modeldes || "-"}
              </p>
              <p>
                <b>{contractPrefix === "3" ? "สี" : "ประเภท"}: </b>
                {arrData?.invtran?.color || "-"}
              </p>
              <p>
                <b>
                  {contractPrefix === "3"
                    ? "เลขตัวถัง"
                    : contractPrefix === "1"
                      ? "เลขโฉนด"
                      : ""}
                  :{" "}
                </b>
                {arrData?.invtran?.strno || "-"}
              </p>
            </>
          )}
        </Col>

        <Col span={12} style={{ textAlign: "center" }}>
          <p>
            <b>วันเริ่มทำสัญญา: </b>
            {formatDateSafe(arrData?.loan?.sdate)}
          </p>

          {isCarOrLeasing ? (
            <>
              <p>
                <b>ชำระงวดแรกเมื่อ: </b>
                {formatDateSafe(arrData?.result[0]?.ddate)}
              </p>
              <p>
                <b>ยอดเช่าซื้อ: </b>
                {currencyFormatPoint(arrData?.loan?.balanc) || "-"} บาท
              </p>
              <p>
                <b>เงินลงทุน: </b>
                {currencyFormatPoint(arrData?.loan?.ncshprc) || "-"} บาท
              </p>
              <p>
                <b>ผ่อนทั้งหมด: </b> {arrData?.loan?.tnopay || "-"} งวด
              </p>
              <p style={{ color: "blue" }}>
                <b>จ่ายมาทั้งหมด: </b>
                {currencyFormatPoint(arrData?.loan?.total_used_net_pay)} บาท
              </p>
              <p style={{ color: "red" }}>
                <b>ลูกหนี้คงเหลือ: </b>
                {currencyFormatPoint(arrData?.loan?.remaining_debt) || 0} บาท
              </p>
            </>
          ) : (
            <>
              <p>
                <b>
                  {dataTypeSelect !== "KSM"
                    ? "กำหนดชำระงวดแรก"
                    : "ชำระครั้งแรกเมื่อ"}
                  :{" "}
                </b>
                {formatDateSafe(
                  arrData?.arpay?.[0]?.ddate || arrData?.resultdata?.[0]?.Inpdt,
                )}
              </p>
              <p>
                <b>ยอดกู้: </b>
                {currencyFormatPoint(arrData?.loan?.ncshprc) || 0} บาท
              </p>
              <p>
                <b>วันที่คิดดอกเบี้ย: </b>
                {arrData?.loan?.flag === 1
                  ? `${formatDateSafe(
                      arrData?.loan?.startdate,
                    )} - ${formatDateSafe(arrData?.loan?.enddate)}`
                  : `${formatDateSafe(
                      dayjs(arrData?.loan?.sdate).add(1, "day"),
                    )} - ${formatDateSafe(arrData?.loan?.enddate)}`}
              </p>
              <p style={{ color: "red" }}>
                <b>ต้นคงเหลือ: </b>
                {currencyFormatPoint(arrData?.loan?.tonkong) || 0} บาท
              </p>
              <p style={{ color: "red" }}>
                <b>ค้างดอกเบี้ย: </b>
                {arrData?.loan?.flag === 1
                  ? currencyFormatPoint(
                      arrData?.loan?.kangdok + arrData?.loan?.dok,
                    ) || 0
                  : currencyFormatPoint(arrData?.loan?.kangdok) || 0}{" "}
                บาท
              </p>
              <p style={{ color: "red" }}>
                <b>รวมทุนฟ้อง: </b>
                {arrData?.loan?.flag === 1
                  ? currencyFormatPoint(
                      arrData?.loan?.kangdok +
                        arrData?.loan?.dok +
                        arrData?.loan?.tonkong,
                    ) || 0
                  : currencyFormatPoint(
                      arrData?.loan?.kangdok + arrData?.loan?.tonkong,
                    ) || 0}{" "}
                บาท
              </p>
            </>
          )}
        </Col>
      </Row>
    );
  };

  const renderTableFooter = () => {
    if (isLeasingType(dataTypeSelect)) {
      return (
        <>
          <p style={{ textAlign: "left", color: "blue" }}>
            จำนวนรับรวม: {currencyFormatPoint(resultLeasing?.totalPaid) || 0}{" "}
            บาท
          </p>
          <p style={{ textAlign: "left", color: "red" }}>
            ค้างค่างวด: {currencyFormatPoint(resultLeasing?.resultNopay) || 0}{" "}
            บาท
          </p>
          <p style={{ textAlign: "left", color: "orange" }}>
            งวดที่ค้าง: {arrData?.loan?.unpaid_count || "-"} งวด
          </p>
          <p style={{ textAlign: "left", color: "orange" }}>
            จำนวนวันที่ค้างรวม:{" "}
            {currencyFormatComma(resultLeasing?.totalLateDate) || 0} วัน
          </p>
        </>
      );
    }

    return (
      <>
        <p style={{ textAlign: "left", color: "orange" }}>
          จำนวนวันที่ค้าง: {arrData?.loan?.days || 0} วัน
        </p>
        <p style={{ textAlign: "left", color: "blue" }}>
          ยอดเงินที่ชำระ: {currencyFormatPoint(result?.totalPayment) || 0} บาท
        </p>
        <p style={{ textAlign: "left", color: "red" }}>
          ดอกเบี้ยที่ชำระ: {currencyFormatPoint(result?.totalKongdok) || 0} บาท
        </p>
        <p style={{ textAlign: "left", color: "green" }}>
          เงินต้นที่ชำระ: {currencyFormatPoint(result?.totalDUETONEFF) || 0} บาท
        </p>
      </>
    );
  };

  const getIconByContractType = () => {
    if (isLeasingType(dataTypeSelect)) return faCarSide;
    const contractPrefix = queryContno?.substring(0, 1);
    if (contractPrefix === "3") return faCarSide;
    if (contractPrefix === "1") return faMapLocationDot;
    return faMagnifyingGlass;
  };

  const getIconColor = () => {
    const contractPrefix = queryContno?.substring(0, 1);
    if (contractPrefix === "3") return "blue";
    if (contractPrefix === "1") return "green";
    return "blue";
  };

  // ============ Main Render ============

  return (
    <Modal
      title="ตารางลูกหนี้"
      open={open}
      onCancel={close}
      width="95%"
      style={{ top: 20 }}
      footer={[
        <Button key="close" onClick={close} icon={<CloseOutlined />}>
          ปิด
        </Button>,
      ]}
    >
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        <Card style={{ marginBottom: "10px" }} size="small">
          <Row>
            <Col span={24} style={{ textAlign: "end" }}>
              <Space wrap size={10}>
                <Tooltip
                  placement="bottom"
                  title="วันที่คิดดอกเบี้ยถึง"
                  arrow={true}
                >
                  <ThaiDatePicker
                    size="large"
                    value={date}
                    onChange={handleDateChange}
                  />
                </Tooltip>

                <Select
                  disabled
                  style={{ width: "auto" }}
                  onChange={handleTypeChange}
                  value={dataTypeSelect}
                  popupMatchSelectWidth={false}
                  options={optionsDataType}
                  size="large"
                  placeholder="เลือกประเภทสัญญา"
                />

                <Search
                  disabled
                  placeholder="ค้นหาสัญญา"
                  onSearch={handleSearch}
                  enterButton
                  value={queryContno}
                  onChange={(e) => handleContnoChange(e.target.value)}
                  style={{ width: 200 }}
                  size="large"
                />
              </Space>
            </Col>
          </Row>
        </Card>

        {arrData ? (
          <Card>
            <Row>
              <Col span={12} style={{ textAlign: "start" }}>
                <b>
                  ผู้ทำสัญญา:{" "}
                  {arrData?.customer?.length > 0
                    ? arrData?.customer[0]?.name
                    : "-"}
                </b>
                <br />
                {arrData?.guarantor?.length > 0 &&
                  queryContno?.substring(0, 1) !== "1" &&
                  arrData.guarantor.map((data, index) => (
                    <React.Fragment key={index}>
                      <b>
                        คนค้ำที่ {data?.garno}: {data?.name}
                      </b>
                      <br />
                    </React.Fragment>
                  ))}
              </Col>

              <Col span={12} style={{ textAlign: "end" }}>
                <Tooltip
                  placement="bottom"
                  title="พิมพ์ข้อมูล PDF"
                  arrow={true}
                >
                  <PrinterOutlined
                    style={{
                      fontSize: "40px",
                      color: "blue",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      isLeasingType(dataTypeSelect)
                        ? generateLeasingDataPDF()
                        : generatePDF();
                    }}
                  />
                </Tooltip>
              </Col>
            </Row>

            <Divider>
              รายละเอียดสัญญา{" "}
              <FontAwesomeIcon
                icon={getIconByContractType()}
                size="2x"
                color={getIconColor()}
                style={{ marginLeft: "10px" }}
              />
            </Divider>

            {renderContractDetails()}

            <Divider />

            <Row>
              <Col span={24}>
                {arrayTable && arrayTable.length > 0 ? (
                  <Table
                    style={{ marginTop: "10px" }}
                    size="small"
                    pagination={false}
                    columns={columns}
                    dataSource={arrayTable}
                    rowSelection={rowSelection}
                    scroll={{ x: 850, y: 400 }}
                    footer={renderTableFooter}
                  />
                ) : (
                  <Empty>
                    <p style={{ color: "red" }}>***ยังไม่มีการจ่ายค่างวด***</p>
                  </Empty>
                )}
              </Col>
            </Row>
          </Card>
        ) : (
          <Empty description="กรุณาค้นหาข้อมูลสัญญา" />
        )}
      </Spin>
    </Modal>
  );
};

export default DebtPayment;
